// eventlist.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import "./eventlist.css";
import EventDetail from "./eventdetail";
import useNotificationStore from "../stores/notificationStore";

const API_URL = import.meta.env.VITE_API_URL;

/* ========================================
 * ヘルパー
 * ======================================== */
const pad2 = (n) => String(n).padStart(2, "0");

const formatDateJP = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} 開催`;
};

/* ========================================
 * コンポーネント
 * ======================================== */
export default function EventList() {
  const [selectedEvent, setSelectedEvent] = useState(null);
  const { showNotification } = useNotificationStore();

  /* ========================================
   * 決済リダイレクト後処理
   * ======================================== */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentSuccess = params.get("payment_success");
    const paymentCanceled = params.get("payment_canceled");
    const eventId = params.get("event_id");

    if (paymentSuccess === "true" && eventId) {
      showNotification(
        "決済が完了しました！エントリーが確定しました。確認メールをご確認ください。",
        "success"
      );

      localStorage.removeItem(`event_cart_${eventId}`);
      window.history.replaceState({}, "", "/enduser/eventlist");
    }

    if (paymentCanceled === "true") {
      showNotification(
        "決済がキャンセルされました。",
        "info"
      );

      window.history.replaceState({}, "", "/enduser/eventlist");
    }
  }, [showNotification]);

  /* ========================================
   * イベント一覧取得
   * ======================================== */
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["events-list"],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/api/events/list/`);
      if (!res.data?.success)
        throw new Error("イベント一覧の取得に失敗しました");
      return res.data.data || [];
    },
    retry: 1,
    staleTime: 0,
    refetchOnMount: "always",
  });

  const events = data || [];

  /* ========================================
   * 詳細表示
   * ======================================== */
  if (selectedEvent) {
    return (
      <EventDetail
        eventId={selectedEvent.event_id}
        onBack={() => setSelectedEvent(null)}
      />
    );
  }

  /* ========================================
   * 一覧表示
   * ======================================== */
  return (
    <div className="eventListPage">
      <div className="eventListCard">
        <div className="eventListHeader">
          <div className="eventListTitle">開催予定のイベント</div>
        </div>

        {isError && (
          <div className="eventListError">
            エラー: {error?.message || "イベント一覧の取得に失敗しました"}
          </div>
        )}

        {isLoading ? (
          <div className="eventListLoading">読み込み中...</div>
        ) : events.length === 0 ? (
          <div className="eventListEmpty">
            開催予定のイベントがありません
          </div>
        ) : (
          <div className="eventListBody">
            {events.map((ev) => (
              <div
                key={ev.event_id}
                className={`eventListRow ${
                  ev.is_entered ? "isEntered" : ""
                }`}
              >
                <div className="eventListRowLeft">
                  <div className="eventListName">
                    {ev.title}{" "}
                    <span className="eventListMeta">
                      （{ev.type_label}）
                      {ev.planned_count > 0 &&
                        `【予定頭数${ev.planned_count}頭】`}
                    </span>

                    {ev.is_entered && (
                      <span className="eventListEnteredBadge">
                        エントリー済（{ev.entry_count}頭）
                      </span>
                    )}
                  </div>
                  <div className="eventListDate">
                    {formatDateJP(ev.date)}
                  </div>
                </div>

                <div className="eventListRowRight">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => setSelectedEvent(ev)}
                  >
                    確認する
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}